import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, CheckCircle2, Scale, ClipboardCheck, Dumbbell, Utensils, Activity, CalendarDays, StickyNote, History, Loader2, FileSignature, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import api, { phasenAPI } from '@/lib/api';
import { datumKurz, komma, delta, ERGEBNIS_TEXT, TERMIN_ART } from '@/components/kundenakte/akteText';
import TestimonialKarte from '@/components/TestimonialKarte';
import { testimonialCrm } from '@/lib/testimonialClient';

/* Startansicht „Übersicht“ der Kundenakte (28.10.2026, Brief 3.2, Reihenfolge = Priorität):
   Nächster Schritt · Kacheln · Aktive Leistungen · Letzter Check-up · Ziele · Termine · Coach-Notiz · Verlauf.
   Alles kommt vorgerechnet aus GET /api/kunden/{id}/akte — die Seite rechnet nichts. Ohne Recht auf Gesundheitsdaten
   liefert der Server null, die Kachel sagt das. */

function Karte({ titel, icon: Icon, children, testid, rechts }) {
  return (
    <section className="rounded-xl border border-border/60 bg-card p-4 space-y-3" data-testid={testid}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
        <h3 className="text-sm font-semibold">{titel}</h3>
        {rechts && <div className="ml-auto">{rechts}</div>}
      </div>
      {children}
    </section>
  );
}

function Kachel({ titel, wert, unter, icon: Icon, testid }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card p-3 min-w-0" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
        {Icon && <Icon className="w-3.5 h-3.5" />}{titel}
      </div>
      <div className="mt-1 text-xl font-bold tabular-nums truncate">{wert}</div>
      {unter && <div className="text-xs text-muted-foreground mt-0.5">{unter}</div>}
    </div>
  );
}

const leer = (text) => <p className="text-xs text-muted-foreground">{text}</p>;

function NaechsterSchritt({ schritt, onAktion }) {
  if (!schritt) return null;
  const ruhig = schritt.regel === 'alles_aktuell';
  return (
    <div className={`rounded-xl border p-4 flex items-center gap-3 ${ruhig ? 'border-border/60 bg-card' : 'border-marke/40 bg-marke/5'}`} data-testid="akte-naechster-schritt">
      {ruhig ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" /> : <ArrowRight className="w-5 h-5 text-marke shrink-0" />}
      <div className="min-w-0 flex-1">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Nächster Schritt</p>
        <p className="font-semibold">{schritt.titel}</p>
        <p className="text-sm text-muted-foreground">{schritt.text}</p>
      </div>
      {schritt.aktion && <Button size="sm" variant="outline" onClick={() => onAktion?.(schritt.aktion)} data-testid="akte-naechster-schritt-knopf">Öffnen</Button>}
    </div>
  );
}

function Kacheln({ k, mitWerten, mitErnaehrung }) {
  const g = k?.gewicht;
  const dl = delta(g?.delta_letzte_kg, 'kg');
  const ds = delta(g?.delta_start_kg, 'kg');
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="akte-kacheln">
      <Kachel testid="kachel-gewicht" icon={Scale} titel="Gewicht"
        wert={!mitWerten ? 'nur mit Recht' : g?.gewicht_kg != null ? `${komma(g.gewicht_kg)} kg` : '–'}
        unter={!mitWerten ? 'Messwerte nur mit Recht Ernährung' : g ? (
          <span className="flex flex-wrap gap-x-2">
            {dl && <span className={dl.klasse}>{dl.text} zur vorigen</span>}
            {ds && <span className={ds.klasse}>{ds.text} seit {datumKurz(g.start_am)}</span>}
            {!dl && !ds && <span>gemessen {datumKurz(g.gemessen_am)}</span>}
          </span>) : 'noch keine Messung'} />
      <Kachel testid="kachel-koerper" icon={Activity} titel="Körperfett · Muskel"
        wert={!mitWerten ? 'nur mit Recht' : g ? `${komma(g.koerperfett_prozent)} % · ${komma(g.muskelmasse_kg)} kg` : '–'}
        unter={!mitWerten ? 'Messwerte nur mit Recht Ernährung' : g ? `Körperanalyse ${datumKurz(g.gemessen_am)}` : null} />
      <Kachel testid="kachel-checkup" icon={ClipboardCheck} titel="Check-up-Kontingent"
        wert={k?.checkup ? `${k.checkup.genutzt ?? 0} von ${k.checkup.inklusive ?? 0}` : '–'}
        unter={k?.checkup ? `genutzt · letzter ${datumKurz(k.checkup.letzter)}` : null} />
      <Kachel testid="kachel-pt" icon={Dumbbell} titel="PT-Einheiten übrig"
        wert={k?.pt ? `${k.pt.uebrig} von ${k.pt.soll}` : '–'} unter={k?.pt ? null : 'kein Coaching-Paket'} />
      <Kachel testid="kachel-ernaehrung" icon={Utensils} titel="Ernährung (7 Tage)"
        wert={!mitErnaehrung ? 'nur mit Recht' : k?.ernaehrung ? `${k.ernaehrung.im_ziel} Tage im Ziel` : '–'}
        unter={!mitErnaehrung ? 'Recht Ernährung' : k?.ernaehrung ? `${k.ernaehrung.geloggt} von 7 Tagen erfasst` : 'kein Ernährungscoaching'} />
      <Kachel testid="kachel-anwesenheit" icon={CalendarDays} titel="Anwesenheit (4 Wochen)"
        wert={k?.anwesenheit ? `${k.anwesenheit.besuche_4w} Besuche` : '–'}
        unter={k?.anwesenheit ? `Ø ${komma(k.anwesenheit.schnitt_woche)} je Woche · davor ${k.anwesenheit.vorher_4w}` : 'keine laufende Mitgliedschaft'} />
    </div>
  );
}

const PHASE_STATUS = {
  laeuft: 'läuft', ergebnis_offen: 'Ergebnis-Check-up steht an', ergaenzung: 'Ergänzung läuft',
  abschluss_offen: 'Abschluss-Check-up steht an', abgeschlossen: 'abgeschlossen',
};

/* Beschleunigungsphase (Coach-Cockpit W1.4): Zeitleiste Woche 1–4 mit Start- und Ergebnis-Check-up, dazu die Monate der
   Ergänzung. Alles vom Server (`phase` in der Akte); hier wird nur gezeichnet. */
export function PhaseKarte({ phase }) {
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState('');
  if (!phase) return null;
  const z = phase.zeitleiste || {};
  const e = phase.ergaenzung;
  const monat2 = async () => {
    setLaeuft(true); setMeldung('');
    try { await phasenAPI.ergaenzungMonat2(phase.id); setMeldung('Zahlungslink für Monat 2 ist unterwegs.'); }
    catch (err) { setMeldung(err?.response?.data?.detail || 'Hat nicht geklappt.'); }
    finally { setLaeuft(false); }
  };
  return (
    <div className="space-y-3" data-testid="akte-phase">
      <p className="text-sm">
        <span className="font-semibold">{phase.status === 'laeuft' && phase.woche ? `Woche ${phase.woche} von ${phase.wochen}` : PHASE_STATUS[phase.status] || phase.status}</span>
        <span className="text-muted-foreground"> · {datumKurz(phase.start_am)} – {datumKurz(phase.ende_am)}</span>
        {phase.pt?.soll ? <span className="text-muted-foreground"> · PT {phase.pt.genutzt}/{phase.pt.soll}{phase.pt.ohne_termin ? `, ${phase.pt.ohne_termin} ohne Termin` : ''}</span> : null}
      </p>
      <ol className="grid grid-cols-4 gap-1.5" aria-label="Zeitleiste der Beschleunigungsphase">
        {(z.wochen || []).map((w) => (
          <li key={w.woche} className={`rounded-lg border px-2 py-1.5 text-xs ${w.aktuell ? 'border-marke bg-marke/10' : w.vorbei ? 'border-border/60 bg-muted/40' : 'border-border/60'}`}
            data-testid={`phase-woche-${w.woche}`}>
            <span className="font-semibold">Woche {w.woche}</span>
            <span className="block text-muted-foreground">{datumKurz(w.von)}</span>
            {w.woche === 1 && <span className="block">{z.start_checkup?.id ? '✓ Start-Check-up' : 'Start-Check-up offen'}</span>}
            {w.woche === 4 && <span className="block">{z.ergebnis_checkup?.id || phase.ergebnis_checkup_id ? '✓ Ergebnis-Check-up' : `Ergebnis ${datumKurz(z.ergebnis_checkup?.am)}`}</span>}
          </li>
        ))}
      </ol>
      {e && (
        <div className="space-y-1.5" data-testid="phase-ergaenzung">
          <p className="text-xs text-muted-foreground">Ergänzung {datumKurz(e.start_am)} – {datumKurz(e.ende_am)}{e.pt?.soll ? ` · PT ${e.pt.genutzt}/${e.pt.soll}` : ''}</p>
          <ol className="grid grid-cols-2 gap-1.5">
            {(z.ergaenzung || []).map((m) => (
              <li key={m.monat} className={`rounded-lg border px-2 py-1.5 text-xs ${m.aktuell ? 'border-marke bg-marke/10' : 'border-border/60'}`}>
                <span className="font-semibold">Monat {m.monat}</span> <span className="text-muted-foreground">{datumKurz(m.von)} – {datumKurz(m.bis)}</span>
              </li>
            ))}
          </ol>
          {e.monat2_offen && (
            <Button size="sm" variant="outline" className="min-h-[44px]" disabled={laeuft} onClick={monat2} data-testid="phase-monat2">
              Zahlungslink Monat 2 senden</Button>
          )}
        </div>
      )}
      {meldung && <p className="text-xs">{meldung}</p>}
    </div>
  );
}

export function Leistungen({ liste }) {
  if (!liste?.length) return leer('Keine aktiven Leistungen.');
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      {liste.map((l, i) => (
        <div key={`${l.art}-${i}`} className="rounded-lg border border-border/60 p-3 text-sm space-y-0.5" data-testid={`akte-leistung-${l.art}`}>
          <p className="font-semibold">{l.titel}</p>
          <p className="text-xs text-muted-foreground">
            {l.von ? `ab ${datumKurz(l.von)}` : 'Start offen'}{l.bis ? ` · bis ${datumKurz(l.bis)}` : ''}
            {l.status && !['active', 'aktiv', 'laufend'].includes(l.status) ? ` · ${l.status}` : ''}
            {l.gekuendigt ? ' · gekündigt' : ''}
          </p>
          {l.rest && <p className="text-xs">{l.rest}</p>}
          {l.naechster_termin && <p className="text-xs">Nächster Termin {datumKurz(l.naechster_termin.datum)}{l.naechster_termin.start ? `, ${l.naechster_termin.start} Uhr` : ''}</p>}
          {l.trainer && <p className="text-xs text-muted-foreground">Trainer: {l.trainer}</p>}
        </div>
      ))}
    </div>
  );
}

const QUELLE_TEXT = { checkup: 'aus dem Check-up', manuell: 'aus der Akte', gratis_ende: 'Verlängerung Gratismonat' };
const euro = (n) => `${Number(n || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export function Angebote({ liste }) {
  if (!liste?.length) return null;
  return (
    <ul className="space-y-1.5 text-sm">
      {liste.map((a) => (
        <li key={a.id} className="flex flex-wrap justify-between gap-2" data-testid="akte-angebot-offen">
          <span><span className="font-medium">{a.titel}</span>{a.quelle ? <span className="text-muted-foreground"> · {QUELLE_TEXT[a.quelle] || a.quelle}</span> : null}</span>
          <span className="text-xs text-muted-foreground tabular-nums">
            {a.summen?.einmalig ? `${euro(a.summen.einmalig)} einmalig` : ''}{a.summen?.einmalig && a.summen?.monatlich ? ' + ' : ''}
            {a.summen?.monatlich ? `${euro(a.summen.monatlich)} je Monat` : ''} · gilt bis {datumKurz(a.gueltig_bis)}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function LetzterCheckup({ c, mitWerten, mitBefund }) {
  if (!c) return leer('Noch kein Check-up.');
  const w = c.werte;
  const e = c.empfehlung;
  return (
    <div className="space-y-2 text-sm">
      <p>
        <span className="font-semibold">{datumKurz(c.datum)}</span>
        {c.trainer ? <span className="text-muted-foreground"> · {c.trainer}</span> : null}
        <span className="text-muted-foreground"> · {c.abgeschlossen ? 'abgeschlossen' : 'noch nicht abgeschlossen'}</span>
      </p>
      {mitWerten && w && (
        <p className="text-xs">Gewicht {komma(w.gewicht_kg)} kg · Körperfett {komma(w.fett_prozent)} % · Muskel {komma(w.muskelmasse_kg)} kg</p>
      )}
      {c.befund ? (
        <p className="text-xs">
          {c.befund.bewertung && <span className="font-medium">{String(c.befund.bewertung).replace('_', ' ')}</span>}
          {c.befund.text ? ` — ${c.befund.text}` : ''}
        </p>
      ) : c.befund_vorhanden && !mitBefund ? leer('Befund vorhanden (nur mit Recht Training oder Ernährung sichtbar).') : null}
      {e && (
        <p className="text-xs">Empfehlung: <span className="font-medium">{typeof e.text === 'string' && e.text ? e.text : '–'}</span>{typeof e.begruendung === 'string' && e.begruendung ? ` · ${e.begruendung}` : ''}</p>
      )}
      {typeof c.ergebnis === 'string' && c.ergebnis && <p className="text-xs">Daraus wurde: <span className="font-medium">{ERGEBNIS_TEXT[c.ergebnis] || c.ergebnis}</span></p>}
      {c.mit_ablauf === false && leer('Eingetragen ohne geführten Ablauf.')}
    </div>
  );
}

function Ziele({ z, mitErnaehrung }) {
  const zeilen = [
    ['Hauptziel', z?.hauptziel || '–'],
    ...(mitErnaehrung ? [
      ['kcal', z?.kcal != null ? komma(z.kcal, 0) : '–'],
      ['Eiweiß / KH / Fett', z?.kcal != null ? `${komma(z.eiweiss, 0)} / ${komma(z.kh, 0)} / ${komma(z.fett, 0)} g` : '–'],
      ['Schritte', z?.schritte != null ? komma(z.schritte, 0) : '–'],
      ['Wasser', z?.wasser_l != null ? `${komma(z.wasser_l)} l` : '–'],
    ] : []),
  ];
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
      {zeilen.map(([t, w]) => (<div key={t} className="contents"><span className="text-muted-foreground">{t}</span><span className="font-medium tabular-nums">{w}</span></div>))}
      {!mitErnaehrung && <p className="col-span-2 text-xs text-muted-foreground">Ernährungsziele nur mit dem Recht Ernährung.</p>}
    </div>
  );
}

export function Termine({ liste }) {
  if (!liste?.length) return leer('Keine kommenden Termine.');
  return (
    <ul className="divide-y divide-border/60 text-sm">
      {liste.map((t, i) => (
        <li key={i} className="py-1.5 flex items-center gap-2">
          <span className="w-24 shrink-0 tabular-nums text-muted-foreground">{t.wochentag} {datumKurz(t.datum)}</span>
          <span className="w-12 shrink-0 tabular-nums">{t.start || ''}</span>
          <span className="rounded bg-muted px-1.5 py-0.5 text-[11px]">{TERMIN_ART[t.art] || t.art}</span>
          <span className="truncate">{t.titel}{t.wer ? ` · ${t.wer}` : ''}{t.status === 'angefragt' ? ' (angefragt)' : ''}</span>
        </li>
      ))}
    </ul>
  );
}

export function CoachNotiz({ leadId, notiz, client = api, basis = '/kunden' }) {
  const [text, setText] = useState(notiz?.text || '');
  const [stand, setStand] = useState(notiz ? `gespeichert ${datumKurz(notiz.geaendert_am)}${notiz.geaendert_von ? ` · ${notiz.geaendert_von}` : ''}` : '');
  const [laeuft, setLaeuft] = useState(false);
  const zuletzt = useRef(notiz?.text || '');
  const uhr = useRef(null);
  useEffect(() => { setText(notiz?.text || ''); zuletzt.current = notiz?.text || ''; }, [leadId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (text === zuletzt.current) return undefined;
    clearTimeout(uhr.current);
    uhr.current = setTimeout(async () => {
      setLaeuft(true);
      try {
        const r = await client.put(`${basis}/${leadId}/coach-notiz`, { text });
        zuletzt.current = text;
        setStand(r.data.coach_notiz ? 'gespeichert' : 'leer');
      } catch (err) {
        setStand(err.response?.data?.detail ? `nicht gespeichert: ${err.response.data.detail}` : 'nicht gespeichert');
      } finally { setLaeuft(false); }
    }, 800);
    return () => clearTimeout(uhr.current);
  }, [text, leadId, client, basis]);
  return (
    <div className="space-y-1">
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={5000}
        placeholder="Nur fürs Team, nie in der App sichtbar" data-testid="akte-coach-notiz" />
      <p className="text-[11px] text-muted-foreground flex items-center gap-1">{laeuft && <Loader2 className="w-3 h-3 animate-spin" />}{laeuft ? 'speichert …' : stand}</p>
    </div>
  );
}

export function Verlauf({ liste }) {
  if (!liste?.length) return leer('Noch nichts im Verlauf.');
  return (
    <ul className="space-y-1.5 text-sm">
      {liste.map((e, i) => (
        <li key={`${e.referenz?.id || i}-${i}`} className="flex gap-2">
          <span className="w-16 shrink-0 tabular-nums text-xs text-muted-foreground pt-0.5">{datumKurz(e.zeit)}</span>
          <span className="min-w-0"><span className="font-medium">{e.titel}</span>{e.text ? <span className="text-muted-foreground"> · {String(e.text).slice(0, 140)}</span> : null}</span>
        </li>
      ))}
    </ul>
  );
}

/* Testimonial-Einwilligung (Coach-Cockpit W1.7): Frage, Zuschnitt, Bibliothek-Freigabe — ohne Recht bleibt die Karte weg */
function TestimonialAkte({ leadId }) {
  const client = useMemo(() => testimonialCrm(leadId), [leadId]);
  return <TestimonialKarte client={client} testId="akte-testimonial" />;
}

/* `client`/`basis`: Die Trainer-Webseite /coach liest dieselbe Akte über ihre Sitzung (`/api/coach/kunden/{id}/…`,
   coach/kundenakte.py) und gibt ihren Client mit — Standard ist das CRM. */
export default function AkteUebersicht({ leadId, akte, fehler, onAktion, onVerlaufMehr, client = api, basis = '/kunden' }) {
  if (fehler) return <p className="text-sm text-muted-foreground" data-testid="akte-fehler">Übersicht nicht geladen: {fehler}</p>;
  if (!akte) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Übersicht lädt …</div>;
  return (
    <div className="space-y-4" data-testid="akte-uebersicht">
      <NaechsterSchritt schritt={akte.naechster_schritt} onAktion={onAktion} />
      {akte.phase && akte.phase.status !== 'abgeschlossen' && (
        <Karte titel={akte.phase.label || 'Beschleunigungsphase'} icon={Rocket} testid="akte-phase-karte"><PhaseKarte phase={akte.phase} /></Karte>
      )}
      <Kacheln k={akte.kacheln} mitWerten={akte.mit_werten} mitErnaehrung={akte.mit_ernaehrung} />
      <Karte titel="Aktive Leistungen" icon={Dumbbell} testid="akte-leistungen"><Leistungen liste={akte.aktive_leistungen} /></Karte>
      {akte.angebote_offen?.length > 0 && (
        <div id="akte-angebote" className="scroll-mt-4">
          <Karte titel="Offene Angebote (in der App)" icon={FileSignature} testid="akte-angebote"><Angebote liste={akte.angebote_offen} /></Karte>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        <Karte titel="Letzter Check-up" icon={ClipboardCheck} testid="akte-letzter-checkup"><LetzterCheckup c={akte.letzter_checkup} mitWerten={akte.mit_werten} mitBefund={akte.mit_befund ?? akte.mit_werten} /></Karte>
        <Karte titel="Ziele & Vorgaben" icon={Activity} testid="akte-ziele"><Ziele z={akte.ziele} mitErnaehrung={akte.mit_ernaehrung} /></Karte>
      </div>
      <Karte titel="Kommende Termine" icon={CalendarDays} testid="akte-termine"><Termine liste={akte.termine} /></Karte>
      {basis === '/kunden' && <TestimonialAkte leadId={leadId} />}{/* /coach zeigt die Karte in der Athlet-Ansicht (Coach-Weg) */}
      <Karte titel="Coach-Notiz" icon={StickyNote} testid="akte-notiz"><CoachNotiz leadId={leadId} notiz={akte.coach_notiz} client={client} basis={basis} /></Karte>
      <Karte titel="Verlauf" icon={History} testid="akte-verlauf"
        rechts={onVerlaufMehr && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onVerlaufMehr}>alles im Zeitstrahl</Button>}>
        <Verlauf liste={akte.verlauf} />
      </Karte>
    </div>
  );
}
